// JD product card — image-cycle arrows + color-swatch swap.
// Shared by every page that renders `card-product-jd` (homepage carousel/grid and
// the collection grid). Pure document-level delegation, no Swiper involved.
// Only a sliding window of 3 images is ever in the DOM: [display-1, display,
// display+1] (the visible image + its previous/next neighbours). Switching only
// toggles `.is-active` (opacity) — neighbours are already loaded, so no flicker.
// `mediaIndex` = the card's resting image; `displayIndex` = what's actually shown
// (hovering the image shows its next neighbour, so displayIndex = mediaIndex+1).
// Guarded by window.__jdCardImageReady so loading it alongside jd-product-carousel.js
// (which contains the same handlers) binds the listeners once.
(function () {
  function mediaUrl(block, idx) {
    const urls = block.dataset.mediaUrls;
    if (!urls) return '';
    const list = urls.split(',');
    if (list.length === 0) return '';
    idx = ((idx % list.length) + list.length) % list.length;
    return list[idx] || '';
  }

  function setActive(block, idx) {
    block
      .querySelectorAll('.pa-card-image:not(.pa-card-image--swatch)')
      .forEach((img) => {
        img.classList.toggle('is-active', img.dataset.imageIndex === String(idx));
      });
  }

  // Keep exactly the [display-1, display, display+1] window of <img> in the DOM.
  function ensureWindow(card, block) {
    const count = parseInt(block.dataset.mediaCount || '0', 10);
    if (count <= 0) return;
    const current = parseInt(block.dataset.displayIndex || block.dataset.mediaIndex || '0', 10);
    const link = block.querySelector('.pa-card-link');
    if (!link) return;

    const indices = new Set();
    if (count === 1) indices.add(0);
    else if (count === 2) {
      indices.add(0);
      indices.add(1);
    } else {
      indices.add((current - 1 + count) % count);
      indices.add(current);
      indices.add((current + 1) % count);
    }

    indices.forEach((idx) => {
      if (block.querySelector(`.pa-card-image[data-image-index="${idx}"]:not(.pa-card-image--swatch)`)) return;
      const img = document.createElement('img');
      img.className = 'pa-card-image pa-card-image--secondary';
      img.dataset.imageIndex = idx;
      img.src = mediaUrl(block, idx);
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      link.appendChild(img);
    });

    block
      .querySelectorAll('.pa-card-image:not(.pa-card-image--swatch)')
      .forEach((img) => {
        const di = parseInt(img.dataset.imageIndex || '-1', 10);
        if (!indices.has(di)) img.remove();
      });
  }

  function showSwatch(block, url) {
    let temp = block.querySelector('.pa-card-image--swatch');
    if (!temp) {
      temp = document.createElement('img');
      temp.className = 'pa-card-image pa-card-image--swatch';
      temp.loading = 'lazy';
      temp.decoding = 'async';
      temp.alt = '';
      const link = block.querySelector('.pa-card-link');
      if (link) link.appendChild(temp);
    }
    if (temp.getAttribute('src') !== url) temp.src = url;
    block
      .querySelectorAll('.pa-card-image')
      .forEach((img) => img.classList.remove('is-active'));
    temp.classList.add('is-active');
  }

  function initCardImages() {
    if (window.__jdCardImageReady) return;
    window.__jdCardImageReady = true;

    // Hover the IMAGE area → show the next image (secondary = current+1).
    document.addEventListener('mouseover', (event) => {
      const media = event.target.closest('.pa-card-media--static');
      if (!media) return;
      if (event.relatedTarget && media.contains(event.relatedTarget)) return;
      const card = media.closest('.jd-card');
      const block = card && card.querySelector('.pa-card-media--static');
      if (!block) return;
      const count = parseInt(block.dataset.mediaCount || '0', 10);
      if (count <= 1) return;
      const base = parseInt(block.dataset.mediaIndex || '0', 10);
      const display = (base + 1) % count;
      block.dataset.displayIndex = display;
      ensureWindow(card, block);
      setActive(block, display);
    });

    document.addEventListener('mouseout', (event) => {
      const media = event.target.closest('.pa-card-media--static');
      if (!media) return;
      const to = event.relatedTarget;
      if (to && media.contains(to)) return;
      const card = media.closest('.jd-card');
      const block = card && card.querySelector('.pa-card-media--static');
      if (!block) return;
      const temp = block.querySelector('.pa-card-image--swatch');
      if (temp) temp.remove();
      const base = parseInt(block.dataset.mediaIndex || '0', 10);
      block.dataset.displayIndex = base;
      setActive(block, base);
    });

    // Prev/next arrows → advance from the image currently shown (displayIndex).
    document.addEventListener('click', (event) => {
      const btn = event.target.closest('.jd-card-media__prev, .jd-card-media__next');
      if (!btn) return;
      const block = btn.closest('.pa-card-media--static');
      const card = btn.closest('.jd-card');
      if (!block || !card) return;
      const count = parseInt(block.dataset.mediaCount || '0', 10);
      if (count <= 1) return;
      let current = parseInt(block.dataset.displayIndex || block.dataset.mediaIndex || '0', 10);
      current = btn.classList.contains('jd-card-media__prev')
        ? (current - 1 + count) % count
        : (current + 1) % count;
      block.dataset.mediaIndex = current;
      block.dataset.displayIndex = current;
      ensureWindow(card, block);
      setActive(block, current);
    });

    // Swatch hover → show the variant image (lazy-created, reused afterwards).
    document.addEventListener('mouseover', (event) => {
      const swatch = event.target.closest('.jd-card .pa-color-swatch');
      if (!swatch || !swatch.dataset.image) return;
      const card = swatch.closest('.jd-card');
      const block = card && card.querySelector('.pa-card-media--static');
      if (block) showSwatch(block, swatch.dataset.image);
    });

    document.addEventListener('mouseout', (event) => {
      const swatch = event.target.closest('.jd-card .pa-color-swatch');
      if (!swatch) return;
      const to = event.relatedTarget;
      if (to && swatch.parentElement && swatch.parentElement.contains(to)) return;
      const card = swatch.closest('.jd-card');
      const block = card && card.querySelector('.pa-card-media--static');
      if (!block) return;
      const temp = block.querySelector('.pa-card-image--swatch');
      if (temp) temp.remove();
      const base = parseInt(block.dataset.mediaIndex || '0', 10);
      block.dataset.displayIndex = base;
      setActive(block, base);
    });
  }

  function init() {
    initCardImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
